import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck, Loader2, Settings } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import NotificationItem from "@/components/NotificationItem";
import { routeFor } from "@/lib/notifMeta";
import { useNotificationStream } from "@/hooks/useNotificationStream";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE = 30;

export default function Notifications() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("all"); // all | unread
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const { lastNotification } = useNotificationStream();

  const load = useCallback(async (offset = 0, append = false) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/notifications`, { params: { limit: PAGE, offset, unread_only: tab === "unread" } });
      setItems((prev) => append ? [...prev, ...(r.data.notifications || [])] : (r.data.notifications || []));
      setTotal(r.data.total || 0);
      setUnread(r.data.unread || 0);
    } catch (err) { console.warn("[Notifications] load failed", err?.message); }
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(0); }, [load]);

  // Real-time: prepend fresh notifications pushed by SSE
  useEffect(() => {
    if (!lastNotification) return;
    setItems((prev) => prev.some((n) => n.notif_id === lastNotification.notif_id) ? prev : [lastNotification, ...prev]);
    setUnread((u) => u + 1);
    setTotal((t) => t + 1);
  }, [lastNotification]);

  const open = async (n) => {
    if (!n.read) {
      setItems((prev) => prev.map((x) => x.notif_id === n.notif_id ? { ...x, read: true } : x));
      setUnread((u) => Math.max(0, u - 1));
      axios.post(`${API}/notifications/${n.notif_id}/read`).catch(() => {});
    }
    navigate(routeFor(n));
  };

  const markAll = async () => {
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try { await axios.post(`${API}/notifications/read-all`); } catch (err) { console.warn("[Notifications] read-all failed", err?.message); }
  };

  const todayStr = new Date().toDateString();
  const groups = { today: [], earlier: [] };
  items.forEach((n) => (new Date(n.created_at).toDateString() === todayStr ? groups.today : groups.earlier).push(n));

  const renderGroup = (label, list) => list.length > 0 && (
    <div key={label}>
      <div className="px-4 pt-3 pb-1 text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      {list.map((n) => <NotificationItem key={n.notif_id} n={n} onClick={open} testidPrefix="inbox" />)}
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="notifications-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Bell className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold">Atividade</h1>
          {unread > 0 && <span className="text-xs bg-red-500 text-white rounded-full px-2 py-0.5 font-semibold" data-testid="inbox-unread-badge">{unread} nova{unread > 1 ? "s" : ""}</span>}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={markAll} disabled={!unread} data-testid="inbox-mark-all">
            <CheckCheck className="w-4 h-4 mr-1" /> Marcar todas como lidas
          </Button>
          <Button variant="ghost" size="icon" onClick={() => navigate("/settings/notifications")} title="Preferências" data-testid="inbox-settings">
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="inline-flex rounded-full border border-border bg-muted/40 p-1" data-testid="inbox-tabs">
        {[["all", "Todas"], ["unread", "Não lidas"]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            data-testid={`inbox-tab-${k}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-primary text-primary-foreground shadow" : "text-muted-foreground hover:text-foreground"}`}
          >
            {label}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden" data-testid="inbox-list">
        {loading && items.length === 0 && <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>}
        {!loading && items.length === 0 && (
          <div className="p-10 text-center text-sm text-muted-foreground" data-testid="inbox-empty">
            {tab === "unread" ? "Tudo em dia! Nenhuma notificação não lida." : "Nenhuma atividade ainda. Quando alguém curtir, comentar, compartilhar ou marcar você, aparece aqui."}
          </div>
        )}
        {renderGroup("Hoje", groups.today)}
        {renderGroup("Anteriores", groups.earlier)}
        {items.length < total && (
          <button
            onClick={() => load(items.length, true)}
            disabled={loading}
            className="w-full py-3 text-sm font-semibold text-primary hover:bg-muted/50 transition-colors"
            data-testid="inbox-load-more"
          >
            {loading ? "Carregando..." : `Carregar mais (${total - items.length})`}
          </button>
        )}
      </Card>
    </div>
  );
}
